import { Button, Card, HStack, Heading, Text, VStack } from '@chakra-ui/react';
import { PayoutBatchClosedReason, type PayoutBatchClosedReasonValue } from '@fluenceur/types';
import { ArrowLeft, TimerOff } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ExpiredStateProps {
  closedReason: PayoutBatchClosedReasonValue | null;
  onBack: () => void;
}

/**
 * The payout was closed before the brand paid for it. Nothing was charged and
 * its commissions are payable again, so the page says so and sends the brand
 * to the list where they now are. No retry: this payout is over for good.
 *
 * Only a missed deadline reads as an expiry. Any other closing (the brand
 * cancelled it, Stripe dropped the payment, its content went stale, or no
 * reason was recorded) reads as a plain cancellation, so the brand is never
 * told it was late when it was not.
 */
export const ExpiredState: React.FC<ExpiredStateProps> = ({ closedReason, onBack }) => {
  const { t } = useTranslation();
  const isExpired = closedReason === PayoutBatchClosedReason.EXPIRED;

  // Literal keys on both sides so the i18n checker sees every one of them.
  const title = isExpired
    ? t('payouts.confirmation.expired.title')
    : t('payouts.confirmation.cancelled.title');
  const message = isExpired
    ? t('payouts.confirmation.expired.message')
    : t('payouts.confirmation.cancelled.message');

  return (
    <VStack gap={6} align="stretch">
      <Card.Root rounded="xl">
        <Card.Body>
          <VStack gap={4} py={6}>
            <TimerOff width={48} height={48} color="var(--chakra-colors-fg-muted)" />
            <VStack gap={2} textAlign="center">
              <Heading size="lg">{title}</Heading>
              <Text color="fg.muted">{message}</Text>
            </VStack>
          </VStack>
        </Card.Body>
      </Card.Root>
      <HStack gap={3}>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft width={20} height={20} />
          {t('payouts.confirmation.expired.backToCommissions')}
        </Button>
      </HStack>
    </VStack>
  );
};
